import { Modal } from '@components/v2/modal'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { Button } from '@mui/material'
import { ApiResponseTemplate, TemplateField } from '@templates/types'
import { AnimatePresence, motion } from 'framer-motion'
import { CheckCircle, Edit, Loader2, Sparkles } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { AIPurpleButton } from './ai-purple-button'
import { Alert, AlertDescription } from './alert'
import { PromptInput } from './prompt-input'
import { ReviewContent } from './review-content'
import { isAxiosError } from 'axios'
import { useRequestPermutations } from './hooks/useRequestPermutations'
import { useGeneratePermutations } from './hooks/useGeneratePermutations'
import { PermutationGenerationResponse } from '@global/api/data-generation'

type Status = 'input' | 'review'

export interface CreateModalProps {
  onClose: () => void
  template: ApiResponseTemplate & { transformedColumns: TemplateField[] }
  onUpdateDatasetData: (data: { fields: number[]; rows: string[][] }) => void
  hasDatasetData: boolean
}

const buildUserErrorMessage = (error: unknown) => {
  if (isAxiosError<{ message?: string }>(error) && error.response?.data.message) {
    return error.response.data.message
  }

  return t`Failed to generate permutations. Please try again.`
}

const MODAL_ANIMATION = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
}

interface ReplaceWarningProps {
  onCancel: () => void
  onConfirm: () => void
  isGenerating: boolean
}

const ReplaceWarning = ({ onCancel, onConfirm, isGenerating }: ReplaceWarningProps) => (
  <>
    <Alert className="bg-yellow-50 border-yellow-200 rounded-lg">
      <AlertDescription className="text-yellow-700 font-medium">
        <Trans>
          Continuing will <strong>replace all existing data</strong> in this submission.
        </Trans>
      </AlertDescription>
    </Alert>
    <div className="pt-4">
      <div className="flex gap-4">
        <Button variant="outlined" onClick={onCancel} className="flex-1 py-3">
          {t`Cancel`}
        </Button>
        <AIPurpleButton onClick={onConfirm} className="flex-1 py-3" disabled={isGenerating}>
          {t`Continue`}
        </AIPurpleButton>
      </div>
    </div>
  </>
)

const ErrorAlert = ({ error }: { error: string }) => (
  <Alert className="bg-red-50 border-red-200 rounded-lg">
    <AlertDescription className="text-red-700 font-medium">{error}</AlertDescription>
  </Alert>
)

const SuccessAlert = () => (
  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
    <Alert className="bg-green-50 border-green-200 rounded-lg">
      <CheckCircle className="h-5 w-5 text-green-600" />
      <AlertDescription className="text-green-700 font-medium">
        {t`Your request has been successfully generated!`}
      </AlertDescription>
    </Alert>
  </motion.div>
)

export const CreateModal = ({
  onClose,
  template,
  onUpdateDatasetData,
  hasDatasetData,
}: CreateModalProps) => {
  const [prompt, setPrompt] = useState('')
  const [showReplaceWarning, setShowReplaceWarning] = useState(false)

  const allFields = useMemo(
    () =>
      template.transformedColumns
        .filter(field => field.governance_type !== 'cv_id' && field.visible_on_form)
        .map(field => field.field),
    [template.transformedColumns],
  )

  const {
    requestPermutations,
    resetRequestPermutations,
    isRequestPermutationsError,
    isRequestPermutationsSuccess,
    isRequestPermutationsLoading,
    exceedsMaxPermutations,
    sessionId,
    generatedFields,
    excludedFields,
    unsupportedFields,
    readOnlyFields,
  } = useRequestPermutations()

  const {
    generatePermutations,
    resetGeneratePermutations,
    isGeneratePermutationsLoading,
    isGeneratePermutationsSuccess,
    isGeneratePermutationsError,
  } = useGeneratePermutations()

  const handleSubmitPrompt = useCallback(() => {
    requestPermutations({ prompt, templateId: template.id, sessionId })
  }, [prompt, requestPermutations, template.id, sessionId])

  const updateDatasetData = useCallback(
    ({ fields, rows }: PermutationGenerationResponse) => {
      const supportedFields = allFields.filter(
        field => !unsupportedFields.includes(field.id) && !excludedFields.includes(field.id),
      )
      const headers = fields
        .map(field => {
          const templateField = supportedFields.find(f => f.name === field)
          return templateField?.id
        })
        .filter((fieldId): fieldId is number => fieldId !== undefined)

      onUpdateDatasetData({ fields: headers, rows })
      setTimeout(onClose, 3000)
    },
    [allFields, excludedFields, onUpdateDatasetData, onClose, unsupportedFields],
  )

  const handleConfirm = useCallback(() => {
    if (generatedFields.length === 0) return

    if (hasDatasetData && !showReplaceWarning) {
      setShowReplaceWarning(true)
      return
    }

    generatePermutations({ fields: generatedFields, sessionId }, { onSuccess: updateDatasetData })
  }, [
    generatedFields,
    hasDatasetData,
    showReplaceWarning,
    generatePermutations,
    sessionId,
    updateDatasetData,
  ])

  const handleEdit = () => {
    resetRequestPermutations()
    resetGeneratePermutations()
  }

  const status = useMemo(
    (): Status => (isRequestPermutationsSuccess ? 'review' : 'input'),
    [isRequestPermutationsSuccess],
  )

  const error = useMemo(() => {
    if (isRequestPermutationsError) {
      return buildUserErrorMessage(isRequestPermutationsError)
    }

    if (isGeneratePermutationsError) {
      return t`Failed to confirm data generation. Please try again.`
    }

    return null
  }, [isRequestPermutationsError, isGeneratePermutationsError])

  const handleCancelReplace = useCallback(() => {
    setShowReplaceWarning(false)
  }, [setShowReplaceWarning])

  return (
    <Modal.Container
      isOpen={true}
      onClickOutside={onClose}
      className="max-w-[640px] flex flex-col bg-white rounded-lg shadow-xl"
      noPadding
    >
      <div className="flex gap-6 flex-col px-8 py-6 h-auto flex-1">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-gray-900">{t`Claravine agent`}</h2>
          <Sparkles className="w-5 h-5 text-purple-600" />
        </div>

        {showReplaceWarning && !isGeneratePermutationsLoading && !isGeneratePermutationsSuccess ? (
          <ReplaceWarning
            onCancel={handleCancelReplace}
            onConfirm={handleConfirm}
            isGenerating={isGeneratePermutationsLoading}
          />
        ) : (
          <>
            {!!error && <ErrorAlert error={error} />}

            <AnimatePresence>
              {status === 'input' && (
                <motion.div {...MODAL_ANIMATION}>
                  <PromptInput
                    prompt={prompt}
                    setPrompt={setPrompt}
                    isLoading={isRequestPermutationsLoading}
                    onSubmit={handleSubmitPrompt}
                  />
                </motion.div>
              )}

              {status === 'review' && generatedFields.length > 0 && (
                <motion.div {...MODAL_ANIMATION}>
                  <ReviewContent
                    prompt={prompt}
                    allFields={allFields}
                    generatedFields={generatedFields}
                    unsupportedFields={unsupportedFields}
                    excludedFields={excludedFields}
                    readOnlyFields={readOnlyFields}
                    isSubmitted={isGeneratePermutationsSuccess}
                    showMaxPermutationsWarning={exceedsMaxPermutations}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {isGeneratePermutationsSuccess && <SuccessAlert />}

            {status === 'review' && !isGeneratePermutationsSuccess && (
              <div className="flex justify-between gap-4 pt-4 border-t border-gray-200">
                <Button
                  variant="outlined"
                  onClick={handleEdit}
                  disabled={isGeneratePermutationsLoading}
                  className="flex-1 py-3"
                  startIcon={<Edit className="h-5 w-5" />}
                >
                  {t`Edit Prompt`}
                </Button>
                <AIPurpleButton
                  onClick={handleConfirm}
                  disabled={isGeneratePermutationsLoading}
                  className="flex-1 py-3"
                >
                  {isGeneratePermutationsLoading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      {t`Generating...`}
                    </>
                  ) : (
                    t`Confirm`
                  )}
                </AIPurpleButton>
              </div>
            )}
          </>
        )}
      </div>
    </Modal.Container>
  )
}
